import React, { useEffect, useState } from 'react';
import PhoneNumbersList from '../../../components/PhoneNumbersList';
import DeleteIcon from '../../../components/Icons/DeleteIcon';
import { toast } from 'react-hot-toast';
import { updateProfileApi } from '../../../redux/Reducers/MyAccountReducer';
import ClientButton from '../../../components/ClientButton';
import { useDispatch, useSelector } from 'react-redux';
import { getProfileDataApi } from '../../../redux/Reducers/ProfileReducer';
import { isEmpty } from 'lodash';
import Loader from '../../../components/Loader';
import { phone_add_plus_one } from '../../../utils/commonUtil';
import PhoneField from '../../../components/PhoneField';
import { TrashIcon } from '@heroicons/react/24/outline';
import Button from '../../../components/Button';

// Simple AddressList for selecting addresses (CREATE/DELETE operations tracked)
const AddressList = ({ options = [], value = [], onChange, member = null }) => {
  const checkedIds = (value || []).map((v) => String(v.service_address_id));
  const toggle = (id) => {
    const exists = checkedIds.includes(String(id));
    let next = [...(value || [])];
    const memberIndex = member?.addresses?.findIndex((item) => item?.id == id);
    if (exists) {
      // remove or mark DELETE
      const idx = next.findIndex((v) => String(v.service_address_id) === String(id));
      if (memberIndex > -1) {
        next[idx] = { ...next[idx], operation: 'DELETE' };
      } else {
        next = next.filter((v) => String(v.service_address_id) !== String(id));
      }
    } else {
      if (memberIndex > -1) {
        // was part of member, ensure it's present with no operation
        next.push({ service_address_id: String(id), operation: null });
      } else {
        next.push({ service_address_id: String(id), operation: 'CREATE' });
      }
    }
    onChange?.(next);
  };
  return (
    <>
      {options?.map((option) => (
        <label className='flex' key={option?.id}>
          <input
            onChange={() => toggle(option?.id)}
            checked={checkedIds.includes(String(option?.id))}
            type='checkbox'
            value={option?.id}
            className='form-checkbox mt-1.5'
          />
          <span className='go-textblacknormal-16 ml-2 pt-0.5'>{option?.full_address}</span>
        </label>
      ))}
    </>
  );
};

export default function SingleMember({ member, deleteModalFn, data, setEdit }) {
  const dispatch = useDispatch();
  const { allServiceAddresses } = useSelector((state) => state.serviceAddresses);
  const [editLoading, setEditLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: member?.first_name || '',
    last_name: member?.last_name || '',
    email: member?.email || '',
    phone_number: member?.phone_numbers?.[0]?.phone_number || '',
    service_addresses: member?.addresses?.map((a) => ({ service_address_id: String(a.id), operation: null })) || [],
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setFormData({
      first_name: member?.first_name || '',
      last_name: member?.last_name || '',
      email: member?.email || '',
      phone_number: member?.phone_numbers?.[0]?.phone_number || '',
      service_addresses: member?.addresses?.map((a) => ({ service_address_id: String(a.id), operation: null })) || [],
    });
  }, [member]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddressesChange = (addresses) => {
    setFormData((prev) => ({ ...prev, service_addresses: addresses }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.first_name?.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) errors.last_name = 'Last name is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.phone_number || formData.phone_number.length < 12) errors.phone_number = 'Phone number is required';
    if (
      !formData.service_addresses ||
      (Array.isArray(formData.service_addresses) && formData.service_addresses.length === 0)
    )
      errors.service_addresses = 'Please select at least one service address';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setEditLoading(true);
    const temp = { id: member?.id, operation: 'UPDATE' };
    if (formData.first_name !== member?.first_name) temp.first_name = formData.first_name;
    if (formData.last_name !== member?.last_name) temp.last_name = formData.last_name;
    if (formData.email !== member?.email) temp.email = formData.email;
    if (formData.phone_number !== member?.phone_numbers?.[0]?.phone_number)
      temp.phone_number = { phone_number: phone_add_plus_one(formData.phone_number) };
    if (formData.service_addresses) {
      const newAddresses = formData.service_addresses.filter((item) => item?.operation === 'CREATE');
      const delAddresses = formData.service_addresses.filter((item) => item?.operation === 'DELETE');
      const addr = [...(newAddresses || []), ...(delAddresses || [])];
      if (addr.length) temp.service_addresses = addr;
    }
    const payload = { members: [temp] };
    const create = updateProfileApi(payload);
    create?.then((res) => {
      if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
        setEditLoading(false);
        toast.success('Member updated successfully!');
        setEdit(false);
        dispatch(getProfileDataApi(false));
      } else {
        setEditLoading(false);
        toast.error(res?.message || 'Something went wrong');
      }
    });
  };

  return (
    <div className='col-span-full rounded-lg bg-white sm:col-span-6 xl:col-span-4'>
      <div className='h-full p-6'>
        {editLoading ? (
          <Loader text='processing...' classes=' h-96' />
        ) : (
          <div className='relative'>
            <div className='absolute -right-4 -top-4'>
              <DeleteIcon onClick={(e) => deleteModalFn(e, true, member)} />
            </div>
            <div className=' flex space-x-2'>
              <div className=' mb-4 flex-row space-y-1.5'>
                <label className='go-label-16 mb-1 block' htmlFor='label'>
                  First Name
                </label>
                <input
                  name='first_name'
                  className={`go-inputtext-16`}
                  type='text'
                  value={formData.first_name}
                  onChange={handleChange}
                />
                {formErrors.first_name && <p className='go-textnormal-14 text-red-500'>{formErrors.first_name}</p>}
              </div>
              <div className=' mb-4 flex-row space-y-1.5'>
                <label className='go-label-16 mb-1 block' htmlFor='label'>
                  Last Name
                </label>
                <input
                  name='last_name'
                  className={`go-inputtext-16`}
                  type='text'
                  value={formData.last_name}
                  onChange={handleChange}
                />
                {formErrors.last_name && <p className='go-textnormal-14 text-red-500'>{formErrors.last_name}</p>}
              </div>
            </div>
            <div className=' mb-4 flex-row space-y-1.5'>
              <label className='go-label-16 mb-1 block' htmlFor='label'>
                Email
              </label>
              <input
                name='email'
                className={`go-inputtext-16 ${formErrors?.email ? 'border-red-500' : ''}`}
                type='email'
                value={formData.email}
                onChange={handleChange}
              />
              {formErrors.email && <p className='go-textnormal-14 text-red-500'>{formErrors.email}</p>}
            </div>
            <div className='mb-4 flex-row space-y-1.5'>
              <PhoneField
                required
                name='phone_number'
                label='Phone'
                value={formData.phone_number}
                onChange={(e, apiValue) => {
                  if (apiValue) setFormData((prev) => ({ ...prev, phone_number: apiValue }));
                  else if (e?.target) handleChange(e);
                }}
                error={formErrors.phone_number}
              />
            </div>
            <div className=' mb-4 flex-row space-y-1.5'>
              <label className='go-label-16  mb-1 block' htmlFor='label'>
                Service Address
              </label>
              <AddressList
                options={allServiceAddresses}
                value={formData.service_addresses}
                onChange={handleAddressesChange}
                member={member}
              />
              {formErrors.service_addresses && (
                <p className='go-textnormal-14 mt-2 text-red-500'>{formErrors.service_addresses}</p>
              )}
            </div>
            {/* <div className='mt-5 flex cursor-pointer text-red-500'>
              <TrashIcon onClick={(e) => deleteModalFn(e, false, member)} className='h-5 w-5 cursor-pointer' />
              <span className='ml-2 font-medium'>Remove Member</span>
            </div> */}
          </div>
        )}
        <div className='flex space-x-4 p-5'>
          <Button variant='secondary' text='Cancel' classes='w-full' onClick={() => setEdit(false)} />
          <Button variant='primary-dark' text='Save' classes='w-full' onClick={onSubmit} />
        </div>
      </div>
    </div>
  );
}
